/* <lazy-embed script-src="..." iframe-src="..."> — third-party facade.
   Nothing loads until the user clicks a [data-embed-load] trigger inside
   the element; then the script and/or iframe are injected once, and the
   static [data-embed-facade] is hidden as soon as the iframe has loaded
   (with a timeout fallback), so the swap reads as in-place.

   Triggers may be <a> or <button>; the handler always calls
   preventDefault() + stopPropagation() so a trigger inside a link (or an
   <a href> trigger itself) never navigates or bubbles.

   Optional trigger attribute:
     data-embed-target="#css-selector" — instead of loading THIS embed,
       smooth-scroll to the target element and load the <lazy-embed>
       found on/inside it (used by the Front Row badge's "Read their
       reviews", which jumps to the clinician-reviews section and boots
       the real widget there, like the live site). Falls back to loading
       this embed if the target doesn't exist on the page (e.g. mini PDP,
       which has no qual reviews section).

   Optional element attributes:
     iframe-id    — id for the injected iframe (vendor scripts look the
                    iframe up by id: Front Row quant expects
                    "iframe-frontrow-quant", qual expects
                    "frontrow-testimonials").
     iframe-class — class list for the injected iframe (Front Row qual
                    resizes iframes matching .iframe-frontrow-testimonials).
     open-message — a message the vendor's script handles to open its popup
                    ("OPEN_MODAL" for Front Row quant, "OPEN_TESTIMONIALS_MODAL"
                    for qual). When set, the tap that loads the embed also opens
                    the popup once the script and its popup frame are ready, so
                    one tap does what used to take two (#574, Eli 2026-10-08).
     open-ready   — selector for the vendor's popup frame to wait for before
                    sending open-message (it opens on that frame's load, or
                    after 2.5 s, whichever is first).
   Reusable for any click-to-load widget (Front Row, chat, etc.). */

const injected = new Set();

class LazyEmbed extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-embed-load]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const selector = trigger.getAttribute('data-embed-target');
        if (selector) {
          const target = document.querySelector(selector);
          if (target) {
            const embed =
              target.closest('lazy-embed') || target.querySelector('lazy-embed');
            if (embed && typeof embed.load === 'function') embed.load();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }
        }
        this.load(true);
      });
    });
  }

  load(fromTap = false) {
    /* Re-clicks while (or after) loading are no-ops — nothing is ever
       double-injected. */
    if (this.hasAttribute('loaded')) return;
    const openMessage = fromTap ? this.getAttribute('open-message') : null;
    this.setAttribute('loaded', '');
    this.setAttribute('aria-busy', 'true');

    const scriptSrc = this.getAttribute('script-src');
    if (scriptSrc && !injected.has(scriptSrc)) {
      injected.add(scriptSrc);
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      if (openMessage) script.addEventListener('load', () => this.openWhenReady(openMessage), { once: true });
      document.head.appendChild(script);
    } else if (openMessage) {
      this.openWhenReady(openMessage);
    }

    const facade = this.querySelector('[data-embed-facade]');
    /* Reserve the facade's current height on the host BEFORE anything is
       injected, so hiding the facade while the vendor iframe streams in
       can't collapse the section (no layout jump / "reload" flash). */
    if (facade && facade.offsetHeight > 0 && !this.style.minHeight) {
      this.style.minHeight = facade.offsetHeight + 'px';
    }
    let swapped = false;
    const swap = () => {
      if (swapped) return;
      swapped = true;
      facade?.setAttribute('hidden', '');
      this.removeAttribute('aria-busy');
    };

    const iframeSrc = this.getAttribute('iframe-src');
    const slot = this.querySelector('[data-embed-slot]');
    if (iframeSrc && slot && !slot.querySelector('iframe')) {
      const iframe = document.createElement('iframe');
      iframe.src = iframeSrc;
      iframe.title = this.getAttribute('embed-title') || 'Embedded content';
      iframe.setAttribute('frameborder', '0');
      iframe.className = this.getAttribute('iframe-class') || 'w-full';
      const iframeId = this.getAttribute('iframe-id');
      if (iframeId && !document.getElementById(iframeId)) iframe.id = iframeId;
      iframe.addEventListener('load', swap, { once: true });
      setTimeout(swap, 4000); // fallback if load never fires
      slot.appendChild(iframe);
    } else {
      swap();
    }
  }

  /* #574: send the vendor's own "open" message once its popup frame exists
     (polled for up to 8 s) and has loaded (or 2.5 s have passed). */
  openWhenReady(message) {
    const readySelector = this.getAttribute('open-ready');
    const started = Date.now();
    const tryOpen = () => {
      const frame = readySelector ? document.querySelector(readySelector) : null;
      if (readySelector && !frame) {
        if (Date.now() - started < 8000) setTimeout(tryOpen, 100);
        return;
      }
      let sent = false;
      const send = () => {
        if (sent) return;
        sent = true;
        window.postMessage(message, '*');
      };
      if (frame) {
        frame.addEventListener('load', send, { once: true });
        setTimeout(send, 2500);
      } else {
        send();
      }
    };
    tryOpen();
  }
}

if (!customElements.get('lazy-embed')) customElements.define('lazy-embed', LazyEmbed);
